import { currencyFormatter } from "@utils/formatter";
import type { ReturnedPaymentOrder } from "@models/user";

// The "Payment Returned" page's copy (#445 / audit C-11), exactly as approved in
// DecisionLog §3.13: Q6 for one order, Q7 for a member who is not the
// organization's Admin, Q8 for two or more orders. Every sentence is one
// string here so the page can't drift from the ruling.

// "A", "A and B", "A, B and C". Hand-rolled: Intl.ListFormat is outside the test
// build's ES2020 lib.
export const joinList = (items: string[]): string =>
    items.length <= 1
        ? items.join("")
        : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const amount = (order: ReturnedPaymentOrder): string =>
    currencyFormatter.format(order.total);

const numbers = (orders: ReturnedPaymentOrder[]): string =>
    joinList(orders.map((order) => order.orderNumberFormatted));

// "the order" / "the orders" in the second sentence (Q8).
const theOrders = (orders: ReturnedPaymentOrder[]): string =>
    orders.length === 1 ? "the order" : "the orders";

// The first sentence, said to everyone whatever their role or the card's state.
export const returnedPaymentLead = (orders: ReturnedPaymentOrder[]): string => {
    const onHold =
        "so your account is on hold and you can't place new orders.";

    if (orders.length === 1) {
        const [order] = orders;
        return `Your bank returned the ${amount(order)} payment for order ${order.orderNumberFormatted}, ${onHold}`;
    }

    const listed = joinList(
        orders.map(
            (order) => `${order.orderNumberFormatted} (${amount(order)})`,
        ),
    );
    return `Your bank returned the payments for orders ${listed}, ${onHold}`;
};

// The organization's Admin, before a card is saved (Q6).
export const returnedPaymentAdminNext = (
    orders: ReturnedPaymentOrder[],
): string =>
    `Add a new card below; we'll contact you to settle ${theOrders(orders)} and restore your account. A returned-payment fee may apply.`;

// Any other member, before a card is saved (Q7): the API refuses their save.
export const returnedPaymentMemberNext = (
    orders: ReturnedPaymentOrder[],
): string =>
    `Ask your account administrator to add a new card; we'll contact them to settle ${theOrders(orders)} and restore your account. A returned-payment fee may apply.`;

// Everyone, once a card has been saved since the return (Q6, Q8; DecisionLog
// §3.13, #445 "Lead rulings for the UI (2026-10-23)").
export const returnedPaymentSaved = (orders: ReturnedPaymentOrder[]): string =>
    `Thanks, your new card is saved. We'll be in touch to settle ${orders.length === 1 ? "order" : "orders"} ${numbers(orders)} and restore your account.`;
